import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  DocumentCartItem,
  fetchDocumentCart,
  fetchDocumentCartCount,
  addFileToCart,
  removeCartItem,
  removeFolderFromCart,
  clearDocumentCart,
  addFolderToCart,
  checkoutDocumentCart,
  CART_SIZE_LIMIT_BYTES,
} from "./document-cart.service";

interface DocumentCartState {
  items: DocumentCartItem[];
  count: number;
  availableCount: number;
  loading: boolean;
  mutating: boolean;
  checkoutLoading: boolean;
  checkoutStatus: "idle" | "processing" | "ready" | "failed";
  checkoutOrderId: string | null;
  checkoutOrderNumber: string | null;
  checkoutError: string | null;
  drawerOpen: boolean;
  badgePulse: boolean;
  companyId: string | null;
  fileIdsInCart: Record<string, true>;
  totalSizeBytes: number;
  maxSizeBytes: number;
  error: string | null;
}

const initialState: DocumentCartState = {
  items: [],
  count: 0,
  availableCount: 0,
  loading: false,
  mutating: false,
  checkoutLoading: false,
  checkoutStatus: "idle",
  checkoutOrderId: null,
  checkoutOrderNumber: null,
  checkoutError: null,
  drawerOpen: false,
  badgePulse: false,
  companyId: null,
  fileIdsInCart: {},
  totalSizeBytes: 0,
  maxSizeBytes: CART_SIZE_LIMIT_BYTES,
  error: null,
};

function indexFileIds(items: DocumentCartItem[]): Record<string, true> {
  const map: Record<string, true> = {};
  for (const item of items) {
    map[item.fileId] = true;
  }
  return map;
}

function getCartErrorCode(err: any, fallback: string): string {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (data && typeof data === "object") {
    const code = data.error || data.code || data.message;
    if (typeof code === "string") return code;
  }
  return err?.response?.statusText || fallback;
}

function markCompanyRequest(
  state: DocumentCartState,
  companyId: string
): boolean {
  if (state.companyId && state.companyId !== companyId) return false;
  state.companyId = companyId;
  return true;
}

export const fetchCartThunk = createAsyncThunk(
  "documentCart/fetch",
  async (companyId: string) => {
    const data = await fetchDocumentCart(companyId);
    return { companyId, ...data };
  }
);

export const fetchCartCountThunk = createAsyncThunk(
  "documentCart/fetchCount",
  async (companyId: string) => {
    const count = await fetchDocumentCartCount(companyId);
    return { companyId, count };
  }
);

export const addFileThunk = createAsyncThunk(
  "documentCart/addFile",
  async (
    {
      companyId,
      fileId,
      source = "document",
    }: {
      companyId: string;
      fileId: string;
      source?: "document" | "profile";
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const data = await addFileToCart(companyId, fileId, source);
      await dispatch(fetchCartThunk(companyId));
      return { companyId, ...data };
    } catch (err: any) {
      return rejectWithValue(getCartErrorCode(err, "ADD_FAILED"));
    }
  }
);

export const addFolderThunk = createAsyncThunk(
  "documentCart/addFolder",
  async (
    {
      companyId,
      categoryId,
      recursive = true,
      categoryIds,
      source = "document",
    }: {
      companyId: string;
      categoryId: string;
      recursive?: boolean;
      categoryIds?: string[];
      source?: "document" | "profile";
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const data = await addFolderToCart(
        companyId,
        categoryId,
        recursive,
        categoryIds,
        source
      );
      await dispatch(fetchCartThunk(companyId));
      return { companyId, ...data };
    } catch (err: any) {
      return rejectWithValue(getCartErrorCode(err, "ADD_FOLDER_FAILED"));
    }
  }
);

export const removeItemThunk = createAsyncThunk(
  "documentCart/removeItem",
  async (
    { companyId, itemId }: { companyId: string; itemId: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const data = await removeCartItem(companyId, itemId);
      await dispatch(fetchCartThunk(companyId));
      return { companyId, ...data };
    } catch (err: any) {
      return rejectWithValue("REMOVE_FAILED");
    }
  }
);

export const removeFolderThunk = createAsyncThunk(
  "documentCart/removeFolder",
  async (
    { companyId, categoryId }: { companyId: string; categoryId: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const data = await removeFolderFromCart(companyId, categoryId);
      await dispatch(fetchCartThunk(companyId));
      return { companyId, ...data };
    } catch (err: any) {
      return rejectWithValue("REMOVE_FOLDER_FAILED");
    }
  }
);

export const clearCartThunk = createAsyncThunk(
  "documentCart/clear",
  async (companyId: string, { rejectWithValue }) => {
    try {
      const data = await clearDocumentCart(companyId);
      return { companyId, ...data };
    } catch (err: any) {
      return rejectWithValue("CLEAR_FAILED");
    }
  }
);

export const checkoutThunk = createAsyncThunk(
  "documentCart/checkout",
  async (companyId: string, { rejectWithValue }) => {
    try {
      const data = await checkoutDocumentCart(companyId);
      return { companyId, ...data };
    } catch (err: any) {
      return rejectWithValue(getCartErrorCode(err, "CHECKOUT_FAILED"));
    }
  }
);

const documentCartSlice = createSlice({
  name: "documentCart",
  initialState,
  reducers: {
    openCartDrawer(state) {
      state.drawerOpen = true;
    },
    closeCartDrawer(state) {
      state.drawerOpen = false;
    },
    toggleCartDrawer(state) {
      state.drawerOpen = !state.drawerOpen;
    },
    clearBadgePulse(state) {
      state.badgePulse = false;
    },
    setCheckoutStatus(
      state,
      action: PayloadAction<{
        status: DocumentCartState["checkoutStatus"];
        orderId?: string;
        orderNumber?: string;
        error?: string;
      }>
    ) {
      state.checkoutStatus = action.payload.status;
      if (action.payload.status === "idle") {
        state.checkoutOrderId = null;
        state.checkoutOrderNumber = null;
        state.checkoutError = null;
      } else {
        if (action.payload.orderId !== undefined) {
          state.checkoutOrderId = action.payload.orderId || null;
        }
        if (action.payload.orderNumber !== undefined) {
          state.checkoutOrderNumber = action.payload.orderNumber || null;
        }
        if (action.payload.error !== undefined) {
          state.checkoutError = action.payload.error || null;
        }
      }
    },
    resetCartState() {
      return initialState;
    },
    /** Socket / external reconcile (Phase 2). */
    applyCartSnapshot(
      state,
      action: PayloadAction<{
        companyId: string;
        count: number;
        availableCount?: number;
        items?: DocumentCartItem[];
        totalSizeBytes?: number;
      }>
    ) {
      if (state.companyId && state.companyId !== action.payload.companyId) {
        return;
      }
      state.companyId = action.payload.companyId;
      state.count = action.payload.count;
      if (action.payload.totalSizeBytes !== undefined) {
        state.totalSizeBytes = action.payload.totalSizeBytes;
      }
      if (action.payload.items) {
        state.items = action.payload.items;
        state.availableCount =
          action.payload.availableCount ??
          action.payload.items.filter((i) => i.available).length;
        state.fileIdsInCart = indexFileIds(action.payload.items);
        if (
          action.payload.items.length > 0 &&
          state.checkoutStatus !== "processing" &&
          state.checkoutStatus !== "ready"
        ) {
          state.checkoutStatus = "idle";
          state.checkoutOrderId = null;
          state.checkoutOrderNumber = null;
        }
      } else if (action.payload.availableCount !== undefined) {
        state.availableCount = action.payload.availableCount;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCartThunk.pending, (state, action) => {
        if (!markCompanyRequest(state, action.meta.arg)) return;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCartThunk.fulfilled, (state, action) => {
        if (state.companyId !== action.payload.companyId) return;
        state.loading = false;
        state.items = action.payload.items;
        state.count = action.payload.count;
        state.availableCount = action.payload.availableCount;
        state.fileIdsInCart = indexFileIds(action.payload.items);
        state.totalSizeBytes = action.payload.totalSizeBytes || 0;
        state.maxSizeBytes =
          action.payload.maxSizeBytes ?? CART_SIZE_LIMIT_BYTES;
        if (
          action.payload.items.length > 0 &&
          state.checkoutStatus !== "processing" &&
          state.checkoutStatus !== "ready"
        ) {
          state.checkoutStatus = "idle";
          state.checkoutOrderId = null;
          state.checkoutOrderNumber = null;
        }
      })
      .addCase(fetchCartThunk.rejected, (state, action) => {
        if (state.companyId !== action.meta.arg) return;
        state.loading = false;
        state.error = "FETCH_FAILED";
      })
      .addCase(fetchCartCountThunk.pending, (state, action) => {
        markCompanyRequest(state, action.meta.arg);
      })
      .addCase(fetchCartCountThunk.fulfilled, (state, action) => {
        if (state.companyId !== action.payload.companyId) return;
        state.count = action.payload.count;
      })
      .addCase(addFileThunk.pending, (state, action) => {
        if (!markCompanyRequest(state, action.meta.arg.companyId)) return;
        state.mutating = true;
        state.error = null;
        // Optimistic: mark file as in cart
        const fileId = action.meta.arg.fileId;
        state.fileIdsInCart[fileId] = true;
        state.count = Object.keys(state.fileIdsInCart).length;
        state.badgePulse = true;
      })
      .addCase(addFileThunk.fulfilled, (state, action) => {
        if (state.companyId !== action.payload.companyId) return;
        state.mutating = false;
        state.count = action.payload.count;
        const existingIdx = state.items.findIndex(
          (i) => i.fileId === action.payload.item.fileId
        );
        if (existingIdx === -1) {
          state.items = [action.payload.item, ...state.items];
        } else {
          state.items[existingIdx] = action.payload.item;
        }
        state.availableCount = state.items.filter((i) => i.available).length;
        state.fileIdsInCart = indexFileIds(state.items);
        if (
          state.checkoutStatus !== "processing" &&
          state.checkoutStatus !== "ready"
        ) {
          state.checkoutStatus = "idle";
          state.checkoutOrderId = null;
          state.checkoutOrderNumber = null;
        }
      })
      .addCase(addFileThunk.rejected, (state, action) => {
        if (state.companyId !== action.meta.arg.companyId) return;
        state.mutating = false;
        state.error = String(action.payload || "ADD_FAILED");
        // Revert optimistic mark — refetch is safest; drop optimistic fileId if not in items
        const fileId = action.meta.arg.fileId;
        if (!state.items.some((i) => i.fileId === fileId)) {
          delete state.fileIdsInCart[fileId];
          state.count = Object.keys(state.fileIdsInCart).length;
        }
      })
      .addCase(addFolderThunk.pending, (state, action) => {
        if (!markCompanyRequest(state, action.meta.arg.companyId)) return;
        state.mutating = true;
        state.error = null;
      })
      .addCase(addFolderThunk.fulfilled, (state, action) => {
        if (state.companyId !== action.payload.companyId) return;
        state.mutating = false;
        state.count = action.payload.count;
        if (action.payload.added > 0) {
          state.badgePulse = true;
        }
        if (
          state.checkoutStatus !== "processing" &&
          state.checkoutStatus !== "ready"
        ) {
          state.checkoutStatus = "idle";
          state.checkoutOrderId = null;
          state.checkoutOrderNumber = null;
        }
      })
      .addCase(addFolderThunk.rejected, (state, action) => {
        if (state.companyId !== action.meta.arg.companyId) return;
        state.mutating = false;
        state.error = String(action.payload || "ADD_FOLDER_FAILED");
      })
      .addCase(removeItemThunk.pending, (state, action) => {
        if (!markCompanyRequest(state, action.meta.arg.companyId)) return;
        state.mutating = true;
        state.error = null;
      })
      .addCase(removeItemThunk.fulfilled, (state, action) => {
        if (state.companyId !== action.payload.companyId) return;
        state.mutating = false;
        const removedId = action.payload.removedId;
        state.items = state.items.filter((i) => i.id !== removedId);
        state.count = action.payload.count;
        state.availableCount = state.items.filter((i) => i.available).length;
        state.fileIdsInCart = indexFileIds(state.items);
      })
      .addCase(removeItemThunk.rejected, (state, action) => {
        if (state.companyId !== action.meta.arg.companyId) return;
        state.mutating = false;
        state.error = "REMOVE_FAILED";
      })
      .addCase(removeFolderThunk.pending, (state, action) => {
        if (!markCompanyRequest(state, action.meta.arg.companyId)) return;
        state.mutating = true;
      })
      .addCase(removeFolderThunk.fulfilled, (state, action) => {
        if (state.companyId !== action.payload.companyId) return;
        state.mutating = false;
        const categoryId = action.payload.categoryId;
        state.items = state.items.filter(
          (i) => i.sourceCategoryId !== categoryId
        );
        state.count = action.payload.count;
        state.availableCount = state.items.filter((i) => i.available).length;
        state.fileIdsInCart = indexFileIds(state.items);
      })
      .addCase(removeFolderThunk.rejected, (state, action) => {
        if (state.companyId !== action.meta.arg.companyId) return;
        state.mutating = false;
        state.error = "REMOVE_FOLDER_FAILED";
      })
      .addCase(clearCartThunk.pending, (state, action) => {
        if (!markCompanyRequest(state, action.meta.arg)) return;
        state.mutating = true;
        state.error = null;
      })
      .addCase(clearCartThunk.fulfilled, (state, action) => {
        if (state.companyId !== action.payload.companyId) return;
        state.mutating = false;
        state.items = [];
        state.count = 0;
        state.availableCount = 0;
        state.fileIdsInCart = {};
        state.totalSizeBytes = 0;
        state.checkoutLoading = false;
        state.checkoutStatus = "idle";
        state.checkoutOrderId = null;
        state.checkoutOrderNumber = null;
        state.checkoutError = null;
      })
      .addCase(clearCartThunk.rejected, (state, action) => {
        if (state.companyId !== action.meta.arg) return;
        state.mutating = false;
        state.error = "CLEAR_FAILED";
      })
      .addCase(checkoutThunk.pending, (state, action) => {
        if (!markCompanyRequest(state, action.meta.arg)) return;
        state.checkoutLoading = true;
        state.checkoutStatus = "processing";
        state.checkoutOrderId = null;
        state.checkoutOrderNumber = null;
        state.checkoutError = null;
      })
      .addCase(checkoutThunk.fulfilled, (state, action) => {
        if (state.companyId !== action.payload.companyId) return;
        state.checkoutLoading = false;
        state.items = [];
        state.count = 0;
        state.availableCount = 0;
        state.fileIdsInCart = {};
        state.totalSizeBytes = 0;
        state.checkoutOrderId = action.payload.order?.id || null;
        state.checkoutOrderNumber =
          action.payload.order?.orderNumber || null;
        state.checkoutStatus =
          action.payload.mode === "sync" ? "ready" : "processing";
      })
      .addCase(checkoutThunk.rejected, (state, action) => {
        if (state.companyId !== action.meta.arg) return;
        state.checkoutLoading = false;
        state.checkoutStatus = "failed";
        state.checkoutError = String(action.payload || "CHECKOUT_FAILED");
      });
  },
});

export const {
  openCartDrawer,
  closeCartDrawer,
  toggleCartDrawer,
  clearBadgePulse,
  setCheckoutStatus,
  resetCartState,
  applyCartSnapshot,
} = documentCartSlice.actions;

export default documentCartSlice.reducer;
